import React, { useState, useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';

const metrics = [
  { num: 500, suffix: '+', title: 'Projects Delivered', desc: 'Successfully shipped digital solutions across various industries.', label: 'Experience', fill: 93, offset: 0 },
  { num: 99, suffix: '.99%', title: 'Hosting Uptime', desc: 'Enterprise-grade reliability ensuring your business is always online.', label: 'Reliability', fill: 55, offset: 145 },
  { num: 24, suffix: '/7', title: 'Technical Support', desc: 'Round-the-clock expert assistance whenever you need it.', label: 'Support', fill: 18, offset: 195 },
  { num: 100, suffix: '+', title: 'Satisfied Clients', desc: 'Building long-term partnerships with businesses worldwide.', label: 'Partnerships', fill: 85, offset: 0 },
  { num: 10, suffix: '+', title: 'Digital Services', desc: 'Comprehensive suite of web, hosting, and AI solutions.', label: 'Capabilities', fill: 60, offset: 145 },
  { num: 5, suffix: '/5', title: 'Client Rating', desc: 'Consistently delivering excellence and exceeding expectations.', label: 'Excellence', fill: 95, offset: 195 },
];

const MetricCard = ({ num, suffix, title, desc, label, fill, offset }) => {
  const [count, setCount] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.2 });

  useEffect(() => {
    if (isInView) {
      const duration = 1200;
      const startTime = performance.now();
      let rAF;

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeOutQuad = progress * (2 - progress);
        setCount(Math.floor(easeOutQuad * num));

        if (progress < 1) {
          rAF = requestAnimationFrame(animate);
        }
      };

      rAF = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(rAF);
    } else {
      setCount(0);
    }
  }, [isInView, num]);

  return (
    <div
      ref={ref}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative w-full h-[540px] rounded-[28px] border border-[rgba(0,0,0,0.08)] bg-[#F5F5F5] overflow-hidden cursor-pointer select-none transition-all duration-300 shadow-[0_4px_12px_rgba(0,0,0,0.01)] hover:shadow-[0_12px_32px_rgba(0,0,0,0.04)]"
    >
      <div className="absolute inset-0 p-6 sm:p-[32px] flex flex-col justify-start text-left z-10">
        <div className="flex flex-col h-full">
          <div style={{ marginTop: `${offset}px` }} className="hidden lg:block transition-all duration-300"></div>
          <div>
            <div className="font-shoulders text-[72px] sm:text-[88px] font-medium leading-none tracking-[1.76px] uppercase text-[#000000]">
              {count}{suffix}
            </div>
            <div className="h-[10px]" />
            <h3 className="font-geist text-[20px] sm:text-[22px] font-semibold leading-[1.5] tracking-[-0.176px] text-[#000000]">
              {title}
            </h3>
            <div className="h-[10px]" />
            <p className="font-geist text-[15px] sm:text-[16px] font-normal leading-[1.5] tracking-[-0.128px] max-w-[304px] text-[#525252]">
              {desc}
            </p>
          </div>
          <div className="flex-1 min-h-[20px]" />
          <div className="font-geist text-[20px] sm:text-[22px] font-semibold leading-[1.5] tracking-[-0.176px] text-[#171717]">
            {label}
          </div>
        </div>
      </div>

      <motion.div
        initial={{ height: 0 }}
        animate={{ height: `${isHovered ? 100 : (isInView ? fill : 0)}%` }}
        transition={{ type: "spring", damping: 35, stiffness: 25, mass: 1.8 }}
        className="absolute bottom-0 left-0 right-0 z-20"
      >
        <div className="absolute inset-0 bg-black h-[1000px] w-full" />
        <div className="absolute top-0 left-0 right-0 -translate-y-[96%] h-[32px] overflow-visible">
          <svg viewBox="0 0 240 28" className="w-[200%] h-full" preserveAspectRatio="none">
            <path d="M0,15 Q30,5 60,15 T120,15 Q150,5 180,15 T240,15 L240,28 L0,28 Z" className="fill-black animate-wave-1" />
            <path d="M0,15 Q30,25 60,15 T120,15 Q150,25 180,15 T240,15 L240,28 L0,28 Z" className="fill-black/35 animate-wave-2" />
          </svg>
        </div>
      </motion.div>

      <motion.div
        initial={{ height: 0 }}
        animate={{ height: `${isHovered ? 100 : (isInView ? fill : 0)}%` }}
        transition={{ type: "spring", damping: 35, stiffness: 25, mass: 1.8 }}
        className="absolute bottom-0 left-0 right-0 z-30 overflow-hidden"
      >
        <div className="absolute bottom-0 left-0 w-full h-[540px] p-6 sm:p-[32px] flex flex-col justify-start text-left pointer-events-none">
          <div className="flex flex-col h-full">
            <div style={{ marginTop: `${offset}px` }} className="hidden lg:block transition-all duration-300"></div>
            <div>
              <div className="font-shoulders text-[72px] sm:text-[88px] font-medium leading-none tracking-[1.76px] uppercase text-white">
                {count}{suffix}
              </div>
              <div className="h-[10px]" />
              <h3 className="font-geist text-[20px] sm:text-[22px] font-semibold leading-[1.5] tracking-[-0.176px] text-white">
                {title}
              </h3>
              <div className="h-[10px]" />
              <p className="font-geist text-[15px] sm:text-[16px] font-normal leading-[1.5] tracking-[-0.128px] max-w-[304px] text-zinc-300">
                {desc}
              </p>
            </div>
            <div className="flex-1 min-h-[20px]" />
            <div className="font-geist text-[20px] sm:text-[22px] font-semibold leading-[1.5] tracking-[-0.176px] text-white">
              {label}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const MetricsSection = () => {
  return (
    <section id="results" className="w-full bg-[#FFFFFF] relative overflow-hidden text-[#000000] selection:bg-neutral-100 border-t border-slate-100">
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Big+Shoulders+Display:wght@100..900&family=Geist:wght@100..900&family=Inter:ital,wght@0,300..900;1,300..900&family=JetBrains+Mono:ital,wght@0,100..800;1,100..800&display=swap');
          .font-shoulders { font-family: 'Big Shoulders Display', sans-serif !important; }
          .font-geist { font-family: 'Geist', sans-serif !important; }
          @keyframes wave-move-left { 0% { transform: translate3d(0,0,0); } 100% { transform: translate3d(-50%,0,0); } }
          @keyframes wave-move-right { 0% { transform: translate3d(-50%,0,0); } 100% { transform: translate3d(0,0,0); } }
          .animate-wave-1 { animation: wave-move-left 6s linear infinite; }
          .animate-wave-2 { animation: wave-move-right 9s linear infinite; }
        `}
      </style>
      <div className="w-full max-w-[1440px] mx-auto px-4 md:px-8 lg:px-16 flex flex-col lg:flex-row gap-0 lg:gap-[36px]">
        <div className="flex flex-col items-start xl:w-[1312px] w-full py-24 mx-auto">
          <div className="flex flex-col gap-[24px] lg:gap-[16px] w-full lg:w-[1312px] mx-auto lg:mx-0">
            <div className="flex flex-col lg:flex-row items-center justify-between w-full gap-[24px] lg:gap-[16px]">
              {metrics.slice(0, 3).map((metric, idx) => (
                <MetricCard key={idx} {...metric} />
              ))}
            </div>
            <div className="flex flex-col lg:flex-row items-center justify-between w-full gap-[24px] lg:gap-[16px]">
              {metrics.slice(3, 6).map((metric, idx) => (
                <MetricCard key={idx} {...metric} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MetricsSection;